import { Button, Typography } from "@mui/material";
import colors from "../Constants/colors";
import DatasetTypeInfo from "../Components/DatasetTypeInfo";

// list to store initial inputs steps
export const getInitialInputSteps = [
  {
    id: "un_01",
    target: '[data-tour="step-one"]', // Use the data attribute to target elements
    spotlightPadding: "10",
    disableBeacon: true,
    content:
      "The 'Inputs node' helps you define your problem statement and set golden responses, building the foundation for optimization and improving accuracy.",
  },
  {
    id: "un_02",
    target: '[data-tour="step-two"]',
    content:
      "Type a problem statement to briefly describe the issue or challenge you're addressing, including key details and its importance to guide the solution",
  },
  {
    target: '[data-tour="step-three"]',
    placement: "right",
    content: (
      <div>
        <p>
          Golden responses are benchmark answers used to evaluate the LLM
          model's accuracy by comparing its outputs to expected results.
        </p>
        <p>
          You can enter multiple Golden Questions by uploading a CSV file with
          the following format:
        </p>

        <table
          style={{
            borderCollapse: "collapse",
            margin: "20px 0",
            width: "100%",
            border: `1px solid ${colors.extremeWhite}`,
            borderRadius: 4,
          }}
        >
          <thead>
            <tr
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = colors.secondaryBlue)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = colors.secondaryBlue)
              }
            >
              <th
                style={{
                  backgroundColor: colors.secondaryBlue,
                  color: "white",
                  padding: "10px",
                  textAlign: "left",
                  border: `1px solid ${colors.extremeWhite}`,
                }}
              >
                Question
              </th>
              <th
                style={{
                  backgroundColor: colors.secondaryBlue,
                  color: "white",
                  padding: "10px",
                  textAlign: "left",
                  border: `1px solid ${colors.extremeWhite}`,
                }}
              >
                Answer
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              style={{ backgroundColor: "#333", color: "#fff" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#333")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#333")
              }
            >
              <td
                style={{
                  padding: "10px",
                  border: `1px solid ${colors.extremeWhite}`,
                }}
              >
                Retrieve candidates with 4 years of experience in Python and
                JavaScript for Fullstack Development.
              </td>
              <td
                style={{
                  padding: "10px",
                  border: `1px solid ${colors.extremeWhite}`,
                }}
              >
                John Doe: Six years of experience in JavaScript and Python.
                <br />
                James A.: Four years of experience in JavaScript and Python.
              </td>
            </tr>
            <tr
              style={{ backgroundColor: "#333", color: "#fff" }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = "#333")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "#333")
              }
            >
              <td
                style={{
                  padding: "10px",
                  border: `1px solid ${colors.extremeWhite}`,
                }}
              >
                Retrieve resumes of data scientists with expertise in machine
                learning.
              </td>
              <td
                style={{
                  padding: "10px",
                  border: `1px solid ${colors.extremeWhite}`,
                }}
              >
                Jane Doe: Five years of experience in machine learning.
                <br />
                Robert B.: Three years of experience in machine learning.
              </td>
            </tr>
          </tbody>
        </table>

        <p>
          The CSV file must contain a 'question' and 'answer' in each row, as
          shown above. Ensure that the data is formatted correctly for smooth
          processing.
        </p>

        <Button
          variant="contained"
          onClick={() => {
            const csvContent = `data:text/csv;charset=utf-8,Question,Answer\nRetrieve candidates with 4 years of experience in Python and JavaScript for Fullstack Development,John Doe: Six years of experience in JavaScript and Python\nRetrieve resumes of data scientists with expertise in machine learning,Jane Doe: Five years of experience in machine learning`;
            const encodedUri = encodeURI(csvContent);
            const link = document.createElement("a");
            link.setAttribute("href", encodedUri);
            link.setAttribute("download", "golden_responses_sample.csv");
            document.body.appendChild(link); // Required for FF
            link.click();
          }}
          sx={{
            marginTop: "10px",
            textTransform: "none",
            color: colors.extremeWhite,
            bgcolor: colors.primaryBlue,
          }}
        >
          Download Sample CSV
        </Button>
        <p>
          Click the <strong>Download Sample CSV</strong> button to download a
          file with the required format.
        </p>
      </div>
    ),
  },
  {
    id: "un_03",
    target: '[data-tour="step-four"]',
    content:
      "Customize how the LLM generates responses to fit your tone, content, and interaction style using Advanced Options.",
  },
];

// list to store initial retrieval steps
export const getInitialRetrievalSteps = [
  {
    id: "un_05",
    target: '[data-tour="option-1b"]',
    disableBeacon: true,
    content:
      "Great! You've successfully added the Retrieval node. Now, let's configure its parameters for optimized data retrieval.",
  },
  {
    id: "un_06",
    target: '[data-tour="option-1c"]',
    placement: "left",
    content: <DatasetTypeInfo />,
    spotlightPadding: 10,
    // options: { showDatasetMedia: true },
  },
  {
    id: "un_07",
    target: '[data-tour="option-1d"]',
    content: (
      <div>
        <p>
          <strong>Confidentiality settings</strong> ensure that documents are
          accessible only to authorized users based on their assigned
          privileges. Levels include:
        </p>
        <ul>
          <li>
            <strong>Public</strong>: Accessible to all users.
          </li>
          <li>
            <strong>Private</strong>: Restricted to specific users or groups.
          </li>
          <li>
            <strong>Confidential</strong>: For highly sensitive data with strict
            access control.
          </li>
        </ul>
      </div>
    ),
    spotlightPadding: 10,
  },
  {
    id: "un_08",
    target: '[data-tour="option-1e"]',
    placement: "left",
    content: (
      <div>
        <p>
          View Datasets allows you to view and manage uploaded datasets. It
          provides two sections:
        </p>
        <ul>
          <li>
            <strong>Datasets without Metadata</strong>: Displays any
            unstructured files (e.g., PDFs) that do not have metadata.
          </li>
          <li>
            <strong>Datasets with Metadata</strong>: Lists datasets with
            associated metadata, offering options to:
            <ul>
              <li>Select a dataset for processing.</li>
              <li>
                Set confidentiality levels (Public, Private, Confidential).
              </li>
              <li>Define metadata and text columns for data interpretation.</li>
              <li>
                Preview the dataset content with paginated navigation
                (Previous/Next).
              </li>
            </ul>
          </li>
        </ul>
        <p>
          This interface enables the organization and management of datasets
          with detailed options for metadata and access control.
        </p>
      </div>
    ),
  },
  {
    id: "un_09",
    target: '[data-tour="option-1f"]',
    content: (
      <div>
        <p>
          Click on the yellow-highlighted <strong>"Expand"</strong> button to
          view the default selected retrieval options.
        </p>
      </div>
    ),
    options: {
      nextButtonName: "Continue →",
    },
    styles: {
      // Custom spotlight styles for this step
      spotlight: {
        backgroundColor: "rgba(255, 204, 0, 0.5)", // Change spotlight color (yellow with transparency)
        boxShadow: "0 0 15px rgba(255, 204, 0, 0.75)", // Add a glowing effect around the spotlight
      },
    },
    spotlightClicks: true,
  },
  {
    id: "un_10",
    target: '[data-tour="option-1g"]',
    content: (
      <div>
        These are the default retrieval options. Review the settings to ensure
        they align with your requirements. Switch to advanced mode to adjust
        options or select multiple settings for greater control and
        customization.
      </div>
    ),
  },
  {
    id: "un_11",
    target: '[data-tour="option-1h"]',
    content: (
      <div>
        Toggle <strong>Advanced Options</strong> to access more detailed
        retrieval settings.
      </div>
    ),
    options: {
      nextButtonName: "Continue →",
    },
    spotlightClicks: true,
    spotlightPadding: 5,
  },
  {
    id: "un_25",
    target: '[data-testid="rf__controls"]',
    content: (
      <div>
        Use these controls if the nodes don't fit on the screen:
        <ul>
          <li>
            <strong>+ Button:</strong> Zooms in
          </li>
          <li>
            <strong>- Button:</strong> Zooms out
          </li>
          <li>
            <strong>&#x26F6; Button:</strong> Adjusts the nodes to fit within
            the screen
          </li>
        </ul>
      </div>
    ),
    spotlightClicks: true,
  },
  {
    id: "un_12",
    target: '[data-tour="option-1i"]',
    content: (
      <div>
        Select from the available retrieval settings to customize your retrieval
        pipeline. You can choose specific options like chunk size, retrieval
        method, and embedding model to tailor the process to your needs. For
        detailed guidance, refer to our{" "}
        <a
          href="https://docs.queryloop.ai/"
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "white", textDecoration: "underline" }}
        >
          documentation
        </a>
        .
      </div>
    ),

    styles: {
      // Custom spotlight styles for this step
      spotlight: {
        boxShadow: "0 0 15px rgba(255, 204, 0, 0.75)", // Add a glowing effect around the spotlight
      },
    },
    spotlightClicks: true,
  },
];

export const handleInputsTutorial = (
  setRun,
  setStepIndex,
  getInitialInputSteps,
  setSteps
) => {
  // Resetting the tour before starting it again
  setRun(false);
  setStepIndex(0);
  let updatedSteps = [...getInitialInputSteps];
  // Additional steps for the tutorial flow
  updatedSteps.push({
    id: "last",
    target: '[data-tour="create-bot-body"]',
    placement: "center",
    disableBeacon: true,
    content: (
      <div>
        <p>
          After adding all nodes required to complete your workflow, connect the
          nodes by dragging from one node's connection point to another.{" "}
        </p>
        <p>
          These connections represent the flow of data between different parts
          of your app.
        </p>
        <p>
          As shown below, the connections visualize how each component
          interacts, ensuring smooth execution of the retrieval and LLM
          processes.
        </p>
      </div>
    ),
    options: {
      nextButtonName: "Finish",
      showDatasetMedia: "show_connections",
      onComplete: () => {
        setRun(false); // Stops the tour after the last step
      },
    },
  });

  setSteps(updatedSteps);
  setRun(true);
};

// list to store initial generation steps
export const getInitialGenerationSteps = (setRun) => [
  {
    id: "un_15",
    target: '[data-tour="option-2a"]',
    disableBeacon: true,
    content:
      "Great! You've successfully added the Generation node. Now, let's configure its parameters for optimized response generation.",
  },
  {
    id: "un_16",
    target: '[data-tour="option-2b"]',
    content: (
      <div>
        Click on the yellow-highlighted <strong>"Expand"</strong> button to
        enlarge the LLM node and view the default selected parameters.
      </div>
    ),
    options: {
      nextButtonName: "Continue →",
    },
    styles: {
      // Custom spotlight styles for this step
      spotlight: {
        backgroundColor: "rgba(255, 204, 0, 0.5)", // Change spotlight color (yellow with transparency)
        boxShadow: "0 0 15px rgba(255, 204, 0, 0.75)", // Add a glowing effect around the spotlight
      },
    },
    spotlightClicks: true,
  },
  {
    id: "un_17",
    target: ".llm-node-minified",
    content: (
      <div>
        <p>
          These are default values for key parameters that control how your
          model generates responses:
        </p>

        <ul>
          <li>
            <strong>Foundation Model</strong>: The base language model used for
            generation, currently set to <code>gpt-4o-mini</code>.
          </li>
          <li>
            <strong>Prompt</strong>: The initial prompt provided to guide the
            model, currently set to 'You are a helpful assistant.'
          </li>
          <li>
            <strong>Fine-tuning</strong>: No fine-tuning is selected by default.
          </li>
        </ul>

        <p>
          These values are set as defaults and can be customized based on your
          specific requirements. Switch to advanced mode to modify these
          settings or enable multiple options for more control over how the
          model generates responses.
        </p>
      </div>
    ),
    placement: "left",
  },
  {
    id: "un_18",
    target: '[data-tour="option-2c"]',
    content: (
      <div>
        <p>
          To customize the generation values, click on the{" "}
          <strong>Advanced Options</strong> toggle.
        </p>
        <p>
          {" "}
          Here, you can adjust settings like the foundational model, prompt
          type, temperature, and more to fine-tune how your model generates
          responses.
        </p>
      </div>
    ),
    options: {
      nextButtonName: "Continue →",
    },
    spotlightClicks: true,
    spotlightPadding: 5,
  },
  {
    id: "un_27",
    target: '[data-testid="rf__controls"]',
    content: (
      <div>
        Use these controls if the nodes don't fit on the screen:
        <ul>
          <li>
            <strong>+ Button:</strong> Zooms in
          </li>
          <li>
            <strong>- Button:</strong> Zooms out
          </li>
          <li>
            <strong>&#x26F6; Button:</strong> Adjusts the nodes to fit within
            the screen
          </li>
        </ul>
      </div>
    ),
    spotlightClicks: true,
  },
  {
    id: "un_20",
    target: '[data-tour="option-2f"]',
    content:
      "Foundation LLMs are large pre-trained models that serve as the base for generating responses, providing general-purpose language capabilities.",
  },
  {
    id: "un_21",
    target: '[data-tour="option-2g"]',
    spotlightPadding: 5,
    content:
      "Temperature controls the randomness of the model's output, with lower values providing more focused responses and higher values offering more creative, varied results.",
  },
  {
    id: "un_22",
    target: '[data-tour="option-2h"]',
    spotlightPadding: 5,
    content:
      "Output token limit defines the maximum number of tokens (words or word fragments) the model can generate, controlling the length of the response.",
  },
  {
    id: "un_23",
    target: '[data-tour="option-2i"]',
    content:
      "Prompt types define how the model generates responses, ranging from zero-shot (no examples) to few-shot and chain-of-thought, which guide the model with examples or reasoning steps.",
  },
  {
    id: "un_24",
    target: '[data-tour="option-2j"]',
    content:
      "The Generate Prompt feature automatically adapts the selected prompt type to align with the problem statement, enhancing the relevance and accuracy of the model's responses.",
  },
  {
    id: "un_19",
    target: '[data-tour="option-2e"]',
    content:
      "Fine-tuning allows you to further train a pre-trained model on custom data to better align its responses with your specific needs.",
  },
  {
    id: "last",
    target: '[data-tour="create-bot-body"]',
    placement: "center",
    disableBeacon: true,
    content: (
      <div>
        <p>
          After adding all nodes required to complete your workflow, connect the
          nodes by dragging from one node's connection point to another.{" "}
        </p>
        <p>
          These connections represent the flow of data between different parts
          of your app.
        </p>
        <p>
          As shown below, the connections visualize how each component
          interacts, ensuring smooth execution of the retrieval and LLM
          processes.
        </p>
      </div>
    ),
    options: {
      nextButtonName: "Finish",
      showDatasetMedia: "show_connections",
      onComplete: () => {
        setRun(false); // Stops the tour after the last step
      },
    },
  },
];

// function to start the onboarding tour
export const startTour = (setRun, setSteps) => {
  setRun(true); // Start the tour
  setSteps([
    {
      id: "welcome-step",
      target: '[data-tour="create-bot-body"]',
      placement: "center",
      disableBeacon: true,
      content: (
        <div>
          <Typography
            variant="h6"
            component="div"
            sx={{ mb: 2, color: colors.extremeWhite }}
          >
            <strong>Welcome to Queryloop</strong>
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6 }}>
            Your all-in-one solution for easily building and optimizing RAG
            applications.
          </Typography>
          <Typography variant="body2" sx={{ mb: 4, lineHeight: 1.6 }}>
            Follow this tutorial to learn the essential steps for creating,
            evaluating, and deploying your LLM-powered apps with ease.
          </Typography>
        </div>
      ),
      options: {
        nextButtonName: "Begin",
      },
    },
    ...getInitialInputSteps,
    {
      id: "un_04",
      target: '[data-tour="step-one"]',
      content:
        "Choose one of the following options to continue the tutorial and learn how to create LLM App: Retrieval Only, Retrieval and Generation, or Generation Only.",
      options: { showButtons: true },
    },
  ]);
};

// New function for the retrieval help button tour
export const handleRetrievalTutorial = (setRun, setStepIndex, setSteps) => {
  // Resetting the tour before starting it again
  setRun(false);
  setStepIndex(0);

  let updatedSteps = [...getInitialRetrievalSteps];
  // Additional steps for the tutorial flow
  updatedSteps.push({
    id: "last",
    target: '[data-tour="create-bot-body"]',
    placement: "center",
    disableBeacon: true,
    content: (
      <div>
        <p>
          After adding all nodes required to complete your workflow, connect the
          nodes by dragging from one node's connection point to another.{" "}
        </p>
        <p>
          These connections represent the flow of data between different parts
          of your app.
        </p>
        <p>
          As shown below, the connections visualize how each component
          interacts, ensuring smooth execution of the retrieval and LLM
          processes.
        </p>
      </div>
    ),
    options: {
      nextButtonName: "Finish",
      showDatasetMedia: "show_connections",
      onComplete: () => {
        setRun(false); // Stops the tour after the last step
      },
    },
  });

  setSteps(updatedSteps);
  setRun(true);
};

// New function for the generation help button tour
export const handleGenerationTutorial = (setRun, setStepIndex, setSteps) => {
  // Resetting the tour before starting it again
  setRun(false);
  setStepIndex(0);

  let updatedSteps = [...getInitialGenerationSteps()];
  // Additional steps for the tutorial flow
  // updatedSteps.push({
  //   id: "un_26",
  //   target: '[data-tour="create-bot-body"]',
  //   content: <div>extra step</div>,
  // });

  setSteps(updatedSteps);
  setRun(true);
};

// show onboarding retrieval steps
export const handleDragDropSuccessRet = (
  setSteps,
  steps,
  selectedOption,
  getInitialRetrievalSteps,
  setRun,
  setStepIndex
) => {
  let prevIndex = steps.count - 1;
  let updatedSteps = [...steps, ...getInitialRetrievalSteps];

  if (selectedOption === "Option 1") {
    updatedSteps.push({
      id: "last",
      target: '[data-tour="create-bot-body"]',
      placement: "center",
      disableBeacon: true,
      content: (
        <div>
          After adding all nodes required to complete your workflow, connect the
          nodes by dragging from one node's connection point to another. These
          connections represent the flow of data between different parts of your
          app.
          <br />
          As shown below, the connections visualize how each component
          interacts, ensuring smooth execution of the retrieval and LLM
          processes.
        </div>
      ),
      options: {
        nextButtonName: "Finish",
        showDatasetMedia: "show_connections",
        onComplete: () => {
          setRun(false); // Stops the tour after the last step
        },
      },
    });
  } else if (selectedOption === "Option 2") {
    updatedSteps.push({
      id: "un_14",
      target: '[data-tour="option-3"]',
      placement: "right",
      content: (
        <div>
          <p>
            Generation helps customize how the LLM creates responses by
            configuring models, prompt settings, and output parameters.
          </p>
          <p>Drag the Generation node to proceed.</p>
        </div>
      ),
      options: {
        nextButtonName: "Continue →",
        showDatasetMedia: "drag_generation",
      },
      spotlightClicks: true,
    });
  }

  setSteps(updatedSteps); // Update the tour steps
  setStepIndex(prevIndex); // Move to the new step (option-1b)
  setRun(true); // Ensure the tour continues
};

// show onboarding generation steps
export const handleDragDropSuccessGen = (
  steps,
  setSteps,
  setStepIndex,
  setRun
) => {
  let prevIndex = steps.count - 1;

  const updatedSteps = [...steps, ...getInitialGenerationSteps()];

  setSteps(updatedSteps); // Update the tour steps
  setStepIndex(prevIndex); // Move to the new step (option-1b)
  setRun(true); // Ensure the tour continues
};
